import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import {
  LogOut,
  Search,
  Bell,
  Home,
  Calendar,
  BarChart2,
  FileText,
  Settings,
  Users,
  X,
  Archive,
  BookOpen,
  Folder,
  Trash2,
  FolderUp,
} from 'lucide-react';
import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { collection, query, where, updateDoc, doc, getDocs, limit } from 'firebase/firestore';
import { filterActive, softDelete } from '../lib/trash';
import { syncReminders, type Reminder } from '../lib/reminders';
import { useWorkspaceTabs } from '../store/workspaceTabsStore';
import {
  countUnread,
  fetchUserNotifications,
} from '../lib/userNotifications';
import type { UserNotification } from '../lib/notificationTypes';
import { formatCaseTitle } from '../lib/utils';
import { getProfilePhotoURL } from '../lib/profilePhoto';
import { getImpersonationModeLabel } from '../lib/impersonationLabels';
import { LexGatWordmark } from './Logo';

const navItems = [
  { name: 'Home', path: '/dashboard', icon: Home },
  { name: 'Cases', path: '/cases', icon: Folder },
  { name: 'Drafts', path: '/drafts', icon: FileText },
  { name: 'Uploads', path: '/uploads', icon: FolderUp },
  { name: 'Library', path: '/library', icon: BookOpen },
  { name: 'Diary', path: '/diary', icon: Calendar },
];

function isNavActive(path: string, pathname: string) {
  if (path === '/dashboard' && pathname === '/dashboard') return true;
  if (path !== '/dashboard' && pathname.startsWith(path)) return true;
  return false;
}

export default function AppMainNav({
  leadingSlot,
  onPlatformNotificationClick,
}: {
  leadingSlot?: ReactNode;
  onPlatformNotificationClick?: (notification: UserNotification) => void;
}) {
  const { user, userData, activeWorkspaceId, logout, impersonatedWorkspaceId, impersonationMode, isAdmin } =
    useAuth();
  const { workspaceData } = useWorkspaceAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [platformNotifications, setPlatformNotifications] = useState<UserNotification[]>([]);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [profilePhotoFailed, setProfilePhotoFailed] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const lastSyncRemindersKeyRef = useRef<string | null>(null);

  const { addTab } = useWorkspaceTabs();

  const openWorkspaceSearch = useCallback(() => {
    addTab({ id: '/workspace/new', path: '/workspace/new', title: 'New Tab', type: 'new' });
    navigate('/workspace/new');
  }, [addTab, navigate]);

  useEffect(() => {
    if (!user) {
      lastSyncRemindersKeyRef.current = null;
      return;
    }

    const ws = activeWorkspaceId || user.uid || 'no-ws';
    const syncKey = `${user.uid}|${ws}`;
    if (lastSyncRemindersKeyRef.current !== syncKey) {
      lastSyncRemindersKeyRef.current = syncKey;
      void syncReminders(user.uid, ws);
    }

    let cancelled = false;

    void (async () => {
      try {
        const q = query(
          collection(db, 'workspaces', ws, 'reminders'),
          where('userId', '==', user.uid),
          where('isRead', '==', false),
          limit(50),
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;

        const data: Reminder[] = [];
        snapshot.forEach((docSnap) => data.push({ id: docSnap.id, ...docSnap.data() } as Reminder));

        const uniqueData = data.filter(
          (rem, index, self) =>
            index === self.findIndex((t) => t.message === rem.message && t.type === rem.type),
        );

        uniqueData.sort((a, b) => {
          const priority = { missed_next_date: 1, upcoming_hearing: 2, overdue: 3, follow_up: 4 };
          return priority[a.type] - priority[b.type];
        });
        setReminders(filterActive(uniqueData));
      } catch (error) {
        console.error('Reminders fetch error:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, activeWorkspaceId]);

  useEffect(() => {
    if (!user?.uid) {
      setPlatformNotifications([]);
      return;
    }

    let cancelled = false;

    void (async () => {
      const rows = await fetchUserNotifications(user.uid);
      if (!cancelled) setPlatformNotifications(rows);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, isNotifOpen]);

  const unreadPlatformCount = useMemo(
    () => countUnread(platformNotifications),
    [platformNotifications],
  );
  const totalNotifCount = reminders.length + unreadPlatformCount;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openWorkspaceSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openWorkspaceSearch]);

  const profilePhotoURL = getProfilePhotoURL(user, userData);

  useEffect(() => {
    setProfilePhotoFailed(false);
  }, [profilePhotoURL]);

  const showProfilePhoto =
    Boolean(profilePhotoURL) && !impersonatedWorkspaceId && !profilePhotoFailed;

  const handlePlatformNotificationClick = (notification: UserNotification) => {
    setIsNotifOpen(false);
    onPlatformNotificationClick?.(notification);
  };

  const handleReminderClick = async (reminder: Reminder) => {
    setIsNotifOpen(false);
    navigate(`/cases/${reminder.caseId}`);
    setReminders((prev) => prev.filter((r) => r.id !== reminder.id));
    try {
      if (!user) return;
      await updateDoc(
        doc(db, 'workspaces', activeWorkspaceId || user?.uid || 'no-ws', 'reminders', reminder.id),
        { isRead: true },
      );
      const qDupes = query(
        collection(db, 'workspaces', activeWorkspaceId || 'no-ws', 'reminders'),
        where('userId', '==', user.uid),
        where('message', '==', reminder.message),
        where('isRead', '==', false),
      );
      const docSnaps = await getDocs(qDupes);
      docSnaps.forEach((d) =>
        updateDoc(
          doc(db, 'workspaces', activeWorkspaceId || user?.uid || 'no-ws', 'reminders', d.id),
          { isRead: true },
        ),
      );
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const handleDeleteReminder = async (
    e: React.MouseEvent | React.TouchEvent,
    reminderId: string,
  ) => {
    e.stopPropagation();
    setReminders((prev) => prev.filter((r) => r.id !== reminderId));
    try {
      if (!user) return;
      const reminder = reminders.find((r) => r.id === reminderId);
      await softDelete(
        doc(db, 'workspaces', activeWorkspaceId || user?.uid || 'no-ws', 'reminders', reminderId),
        {
          deletedBy: user.uid,
          entityType: 'reminder',
          title: reminder?.message || 'Reminder',
        },
      );
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  return (
    <>
      <header className="app-shell-inset sticky top-0 z-50 flex h-14 w-full shrink-0 items-center gap-2 md:gap-2.5">
        {leadingSlot ? <div className="flex shrink-0 items-center md:hidden">{leadingSlot}</div> : null}

        <Link
          to="/dashboard"
          className="bg-[#333333] rounded-md flex h-14 items-center justify-center shrink-0 px-3 hover:bg-[#3d3d3d] transition-colors"
        >
          <LexGatWordmark className="h-7 sm:h-8 w-auto" tone="on-dark" />
        </Link>

        <nav className="bg-[#333333] rounded-md hidden md:flex flex-1 h-14 items-stretch justify-center gap-6 lg:gap-10 px-6 overflow-x-auto hide-scrollbar">
          {navItems.map((item) => {
            const active = isNavActive(item.path, location.pathname);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center h-full px-3 text-sm font-normal transition-colors whitespace-nowrap ${
                  active ? 'text-white' : 'text-neutral-300 hover:text-white'
                }`}
              >
                {item.name}
                {active && (
                  <motion.span
                    layoutId="main-nav-indicator"
                    layout
                    className="main-nav-indicator absolute bottom-0 left-0 w-full h-[3px] rounded-t-md bg-[#3B82F6] shadow-[0_-2px_8px_rgba(59,130,246,0.25)]"
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 34,
                      mass: 0.8,
                    }}
                    aria-hidden
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="md:hidden flex-1 min-w-0" aria-hidden />

        <div className="bg-[#333333] rounded-md flex h-14 items-center justify-center shrink-0 px-3 sm:px-4 gap-2 sm:gap-3">
          <button
            onClick={openWorkspaceSearch}
            className="flex h-10 w-10 items-center justify-center text-white hover:text-neutral-200 transition-colors"
            aria-label="Search"
          >
            <Search className="w-5 h-5" strokeWidth={1.75} />
          </button>

          <div className="relative flex items-center justify-center" ref={notifRef}>
            <button
              onClick={() => setIsNotifOpen(!isNotifOpen)}
              className="flex h-10 w-10 items-center justify-center text-white hover:text-neutral-200 transition-colors relative"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" strokeWidth={1.75} />
              {totalNotifCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-[#333333]">
                  {totalNotifCount > 9 ? '9+' : totalNotifCount}
                </span>
              )}
            </button>

            {isNotifOpen && (
              <div className="absolute right-0 top-full mt-4 w-80 bg-white rounded-xl shadow-2xl border border-border-light overflow-hidden z-50">
                <div className="p-4 border-b border-border-light flex justify-between items-center bg-bg-secondary">
                  <h3 className="font-semibold text-text-main">Notifications</h3>
                  <span className="text-xs font-medium bg-border-light text-[#475569] px-2 py-1 rounded-full">
                    {totalNotifCount} New
                  </span>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {platformNotifications.length === 0 && reminders.length === 0 ? (
                    <div className="p-6 text-center text-[#64748B] text-sm">No notifications</div>
                  ) : (
                    <div className="flex flex-col">
                      {platformNotifications.length > 0 ? (
                        <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] bg-bg-secondary border-b border-border-light">
                          Platform messages
                        </div>
                      ) : null}
                      {platformNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="border-b border-border-light hover:bg-bg-secondary transition-colors last:border-none"
                        >
                          <button
                            type="button"
                            onClick={() => handlePlatformNotificationClick(notification)}
                            className="p-4 flex-1 text-left flex flex-col gap-1 w-full outline-none"
                          >
                            <div className="flex items-center justify-between gap-2 w-full">
                              <div className="flex items-center gap-2">
                                {!notification.read ? (
                                  <span className="h-2 w-2 rounded-full bg-primary shrink-0" aria-hidden />
                                ) : null}
                                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                                  Platform
                                </span>
                              </div>
                              <span className="text-[10px] text-[#94A3B8]">
                                {new Date(notification.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p
                              className={`text-sm font-medium pr-2 ${
                                notification.read ? 'text-[#64748B]' : 'text-text-main'
                              }`}
                            >
                              {notification.title}
                            </p>
                            <p className="text-xs text-[#64748B] line-clamp-2">{notification.body}</p>
                          </button>
                        </div>
                      ))}
                      {reminders.length > 0 && platformNotifications.length > 0 ? (
                        <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] bg-bg-secondary border-b border-border-light">
                          Case reminders
                        </div>
                      ) : null}
                      {reminders.map((reminder) => (
                        <div
                          key={reminder.id}
                          className="border-b border-border-light hover:bg-bg-secondary transition-colors last:border-none flex items-start group"
                        >
                          <button
                            onClick={() => handleReminderClick(reminder)}
                            className="p-4 flex-1 text-left flex flex-col gap-1 w-full outline-none"
                          >
                            <div className="flex items-center justify-between gap-2 w-full">
                              <span
                                className={`text-xs font-bold uppercase tracking-wider ${
                                  reminder.type === 'missed_next_date'
                                    ? 'text-red-600'
                                    : reminder.type === 'upcoming_hearing'
                                      ? 'text-blue-600'
                                      : reminder.type === 'overdue'
                                        ? 'text-orange-600'
                                        : 'text-purple-600'
                                }`}
                              >
                                {reminder.type.replace(/_/g, ' ')}
                              </span>
                              <span className="text-[10px] text-[#94A3B8]">
                                {new Date(reminder.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-text-main pr-2">{reminder.message}</p>
                            <p className="text-xs text-[#64748B] truncate w-full">
                              {formatCaseTitle(reminder.caseTitle)}
                            </p>
                          </button>
                          <button
                            onClick={(e) => handleDeleteReminder(e, reminder.id)}
                            className="p-3 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 mt-2 mr-1"
                            title="Dismiss"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="w-px h-6 bg-white/15 hidden sm:block" />

          <div className="relative flex items-center" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`h-8 w-8 shrink-0 overflow-hidden rounded-full p-0 focus:outline-none transition-transform hover:scale-105 active:scale-95 ${
                showProfilePhoto
                  ? 'bg-[#333333]'
                  : 'flex items-center justify-center bg-gradient-to-tr from-blue-500 to-teal-400 text-xs font-bold text-white shadow-lg'
              }`}
            >
              {impersonatedWorkspaceId ? (
                workspaceData?.firmName?.charAt(0) || workspaceData?.name?.charAt(0) || 'W'
              ) : showProfilePhoto ? (
                <img
                  src={profilePhotoURL!}
                  alt="Profile"
                  className="block h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={() => setProfilePhotoFailed(true)}
                />
              ) : (
                userData?.displayName?.charAt(0) ||
                user?.displayName?.charAt(0) ||
                user?.email?.charAt(0)?.toUpperCase() ||
                'L'
              )}
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {impersonatedWorkspaceId
                      ? `${workspaceData?.firmName || 'Workspace'} (Impersonated)`
                      : userData?.displayName || user?.displayName || 'Lawyer'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {impersonatedWorkspaceId && impersonationMode
                      ? getImpersonationModeLabel(impersonationMode)
                      : user?.email}
                  </p>
                </div>
                <div className="p-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/analytics');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <BarChart2 className="w-4 h-4" />
                    Analytics
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/closed-cases');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Archive className="w-4 h-4" />
                    Closed Cases
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/recycle-bin');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Recycle Bin
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/team');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    Team
                  </button>
                  {isAdmin ? (
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        navigate('/admin');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-700 font-bold hover:bg-indigo-50 rounded-md transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Admin Panel
                    </button>
                  ) : null}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 font-medium hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                </div>
                <div className="p-1 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 font-medium hover:bg-red-50 rounded-md transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="fixed bottom-0 left-0 right-0 z-50 h-16 bg-[#222222]/95 backdrop-blur-xl border-t border-white/10 md:hidden flex items-center justify-around px-2 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_24px_rgba(0,0,0,0.1)]">
        {navItems.map((item) => {
          const active = isNavActive(item.path, location.pathname);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center gap-1 w-full h-full relative ${
                active ? 'text-[#3B82F6]' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <item.icon className={`w-5 h-5 transition-transform ${active ? 'scale-110' : ''}`} />
              <span className="text-[10px] font-medium">{item.name}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}
